// Phase 6 — KPI cards + 5-bucket dR distribution histogram.
//
// Four scalar cards (total / mean / best config / worst station)
// sit in a CSS grid; below them a stack of 5 horizontal bars
// renders the bucket distribution. All numbers in mono font per
// the Phase-4.13.2 design system.
//
// Phase 6.6 C — the min/max numbers on the «всего задач» card
// become clickable deep-links to /job/<id>. Phase 6.6 D — the
// «n=N» tail on the highlight cards is replaced with the
// operator-friendly «N задач» (with correct Russian plural form).

import { useNavigate } from 'react-router-dom';

import type { AnalyticsSummary } from '../../api/types';
import { pluralize_jobs } from '../../lib/pluralize_ru';
import { formatConfig } from './formatConfig';

export function SummaryCards({ data }: { data: AnalyticsSummary }) {
  const navigate = useNavigate();
  const hasAny = data.total_residuals > 0;
  // Phase 6.6 C — guarded deep-links: only enable the click handler
  // when the backend shipped a non-null job_id (empty cohort or
  // pre-6.6 backends both return null). Fall back to a non-clickable
  // span so the visual layout doesn't shift between states.
  const minClickable = hasAny && data.min_job_id !== null;
  const maxClickable = hasAny && data.max_job_id !== null;
  const minTooltip =
    data.min_station && data.min_config
      ? `${data.min_station} × ${formatConfig(data.min_config)} — открыть карточку →`
      : undefined;
  const maxTooltip =
    data.max_station && data.max_config
      ? `${data.max_station} × ${formatConfig(data.max_config)} — открыть карточку →`
      : undefined;
  return (
    <div className="analytics-summary">
      <div className="summary-cards-grid">
        <div className="summary-card" data-testid="summary-card-total">
          <div className="summary-label">всего задач</div>
          <div className="summary-value">{data.total_residuals}</div>
          {hasAny && (
            <div className="summary-sub">
              min{' '}
              {minClickable ? (
                <button
                  type="button"
                  className="summary-extreme-link"
                  title={minTooltip}
                  onClick={() => navigate(`/job/${data.min_job_id}`)}
                  data-testid="summary-min-link"
                >
                  {data.min_dr_3d_mm.toFixed(1)}
                </button>
              ) : (
                <span>{data.min_dr_3d_mm.toFixed(1)}</span>
              )}
              {' · max '}
              {maxClickable ? (
                <button
                  type="button"
                  className="summary-extreme-link"
                  title={maxTooltip}
                  onClick={() => navigate(`/job/${data.max_job_id}`)}
                  data-testid="summary-max-link"
                >
                  {data.max_dr_3d_mm.toFixed(1)}
                </button>
              ) : (
                <span>{data.max_dr_3d_mm.toFixed(1)}</span>
              )}
              {' мм'}
            </div>
          )}
        </div>

        <div className="summary-card" data-testid="summary-card-avg">
          <div className="summary-label">средний |dR|</div>
          <div className="summary-value">
            {hasAny ? data.avg_dr_3d_mm.toFixed(1) : '—'}
            <span className="unit">мм</span>
          </div>
        </div>

        <div className="summary-card" data-testid="summary-card-best-config">
          <div className="summary-label">лучший конфиг</div>
          {data.best_config ? (
            <>
              <div className="summary-value-small">
                {formatConfig(data.best_config.name)}
              </div>
              <div className="summary-sub">
                {data.best_config.avg_dr_mm.toFixed(1)} мм ·{' '}
                {pluralize_jobs(data.best_config.count)}
              </div>
            </>
          ) : (
            <div className="summary-value-small">—</div>
          )}
        </div>

        <div className="summary-card" data-testid="summary-card-worst-station">
          <div className="summary-label">худшая станция</div>
          {data.worst_station ? (
            <>
              <div className="summary-value-small">{data.worst_station.name}</div>
              <div className="summary-sub">
                {data.worst_station.avg_dr_mm.toFixed(1)} мм ·{' '}
                {pluralize_jobs(data.worst_station.count)}
              </div>
            </>
          ) : (
            <div className="summary-value-small">—</div>
          )}
        </div>
      </div>

      {hasAny && (
        <div
          className="bucket-distribution"
          role="list"
          aria-label="Распределение |dR| по диапазонам"
        >
          {data.buckets.map((b) => (
            <div className="bucket-row" key={b.range} role="listitem">
              <div className="bucket-label-left">{b.range}</div>
              <div className="bucket-bar-track">
                <div
                  className="bucket-bar-fill"
                  style={{ width: `${b.pct}%` }}
                  aria-label={`${b.count} (${b.pct}%)`}
                />
              </div>
              <div className="bucket-label-right">
                {b.count}{' '}
                <span className="bucket-pct">({b.pct}%)</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
